import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X, LogOut, Image as ImageIcon, Tag, Mail } from "lucide-react";
import { format } from "date-fns";
import api, { getImageUrl } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { AnalyticsDashboard } from "@/components/admin/AnalyticsDashboard";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tours, setTours] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [editTour, setEditTour] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // Cancel booking dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelFullRefund, setCancelFullRefund] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const getCancelRefundInfo = (booking) => {
    if (!booking) return { amount: 0, percent: 0, reason: "" };
    const total = booking.total_price || 0;
    try {
      const tourDate = new Date(booking.date + "T00:00:00Z");
      const now = new Date();
      const hoursUntil = (tourDate - now) / (1000 * 60 * 60);
      if (hoursUntil >= 48) return { amount: total, percent: 100, reason: "Full refund (48+ hours before tour)" };
      if (hoursUntil >= 24) return { amount: Math.round(total * 0.5 * 100) / 100, percent: 50, reason: "50% refund (24-48 hours before tour)" };
      return { amount: 0, percent: 0, reason: "No refund (less than 24 hours before tour)" };
    } catch { return { amount: total, percent: 100, reason: "Full refund" }; }
  };

  const openCancelDialog = (booking) => {
    setCancelBooking(booking);
    setCancelReason("");
    setCancelFullRefund(false);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!cancelBooking) return;
    setCancelling(true);
    try {
      await api.post(`/bookings/${cancelBooking.booking_id}/cancel`, {
        reason: cancelReason || "Admin cancellation",
        full_refund: cancelFullRefund,
        refund_reason: cancelFullRefund ? "Full refund (admin override - weather/operational)" : undefined
      });
      toast.success("Booking cancelled. Cancellation email sent to customer.");
      setCancelDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [longDescription, setLongDescription] = useState("");
  const [category, setCategory] = useState("Land");
  const [price, setPrice] = useState("");
  const [duration, setDuration] = useState("");
  const [durationHours, setDurationHours] = useState("");
  const [difficulty, setDifficulty] = useState("Easy");
  const [maxCapacity, setMaxCapacity] = useState("20");
  const [meetingPoint, setMeetingPoint] = useState("");
  const [images, setImages] = useState([]);
  const [whatsIncluded, setWhatsIncluded] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [newIncluded, setNewIncluded] = useState("");
  const [newHighlight, setNewHighlight] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [childrenAllowed, setChildrenAllowed] = useState(true);
  const [childPrice, setChildPrice] = useState("");

  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogFormOpen, setBlogFormOpen] = useState(false);
  const [blogMode, setBlogMode] = useState("add");
  const [editPost, setEditPost] = useState(null);
  const [blogTitle, setBlogTitle] = useState("");
  const [blogExcerpt, setBlogExcerpt] = useState("");
  const [blogImage, setBlogImage] = useState("");
  const [blogCategory, setBlogCategory] = useState("Travel Guide");
  const [blogReadTime, setBlogReadTime] = useState("5 min read");
  const [blogContent, setBlogContent] = useState("");
  const [blogSaving, setBlogSaving] = useState(false);

  // Banner state
  const [banners, setBanners] = useState([]);
  const [bannerFormOpen, setBannerFormOpen] = useState(false);
  const [bannerMode, setBannerMode] = useState("add");
  const [editBanner, setEditBanner] = useState(null);
  const [bannerTitle, setBannerTitle] = useState("");
  const [bannerDesc, setBannerDesc] = useState("");
  const [bannerCode, setBannerCode] = useState("");
  const [bannerPercent, setBannerPercent] = useState("");
  const [bannerBg, setBannerBg] = useState("#0F172A");
  const [bannerText, setBannerText] = useState("#C5A059");
  const [bannerStart, setBannerStart] = useState("");
  const [bannerEnd, setBannerEnd] = useState("");
  const [bannerEnabled, setBannerEnabled] = useState(true);
  const [bannerSaving, setBannerSaving] = useState(false);

  // Inventory state
  const [inventoryTour, setInventoryTour] = useState(null);
  const [inventoryDate, setInventoryDate] = useState(undefined);
  const [inventoryCapacity, setInventoryCapacity] = useState("");
  const [inventoryAvailability, setInventoryAvailability] = useState(null);

  const fetchInventoryAvailability = async (tourId) => {
    if (!tourId) return;
    try {
      const res = await api.get(`/tours/${tourId}/availability`);
      setInventoryAvailability(res.data);
    } catch {
      setInventoryAvailability(null);
    }
  };

  // Pricing rules state
  const [pricingRules, setPricingRules] = useState(null);
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [userRes, toursRes, bookingsRes, rulesRes, leadsRes, blogRes, analyticsRes, bannersRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/tours"),
        api.get("/bookings"),
        api.get("/pricing-rules"),
        api.get("/leads"),
        api.get("/blog").catch(() => ({ data: [] })),
        api.get("/analytics").catch(() => ({ data: null })),
        api.get("/banners").catch(() => ({ data: [] })),
      ]);
      setUser(userRes.data);
      setTours(Array.isArray(toursRes.data) ? toursRes.data : []);
      setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
      setPricingRules(rulesRes.data);
      setLeads(Array.isArray(leadsRes.data) ? leadsRes.data : []);
      setBlogPosts(Array.isArray(blogRes.data) ? blogRes.data : []);
      setAnalytics(analyticsRes.data);
      setBanners(Array.isArray(bannersRes.data) ? bannersRes.data : []);
    } catch {
      toast.error("Failed to load data");
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => {
    await api.post("/auth/logout");
    navigate("/");
  };

  const resetForm = () => {
    setTitle(""); setDescription(""); setLongDescription(""); setCategory("Land");
    setPrice(""); setDuration(""); setDurationHours(""); setDifficulty("Easy");
    setMaxCapacity("20"); setMeetingPoint(""); setImages([]); setWhatsIncluded([]);
    setHighlights([]); setNewIncluded(""); setNewHighlight("");
    setChildrenAllowed(true); setChildPrice("");
  };

  const openAdd = () => {
    resetForm();
    setFormMode("add");
    setEditTour(null);
    setFormOpen(true);
  };

  const openEdit = (tour) => {
    setFormMode("edit");
    setEditTour(tour);
    setTitle(tour.title); setDescription(tour.description);
    setLongDescription(tour.long_description || ""); setCategory(tour.category);
    setPrice(String(tour.price)); setDuration(tour.duration);
    setDurationHours(String(tour.duration_hours || "")); setDifficulty(tour.difficulty);
    setMaxCapacity(String(tour.max_capacity)); setMeetingPoint(tour.meeting_point || "");
    setImages(tour.images || []); setWhatsIncluded(tour.whats_included || []);
    setHighlights(tour.highlights || []);
    setChildrenAllowed(tour.children_allowed !== false);
    setChildPrice(String(tour.child_price || ""));
    setFormOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData);
      setImages([...images, res.data.url]);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!title || !description || !price || !duration) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    const payload = {
      title, description, long_description: longDescription, category,
      price: parseFloat(price), child_price: parseFloat(childPrice) || 0,
      children_allowed: childrenAllowed, duration, duration_hours: parseFloat(durationHours) || 3,
      difficulty, max_capacity: parseInt(maxCapacity) || 20, meeting_point: meetingPoint,
      images, whats_included: whatsIncluded, highlights,
    };
    try {
      if (formMode === "add") {
        await api.post("/tours", payload);
        toast.success("Tour created");
      } else {
        await api.put(`/tours/${editTour.tour_id}`, payload);
        toast.success("Tour updated");
      }
      setFormOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tourId) => {
    setDeleting(tourId);
    try {
      await api.delete(`/tours/${tourId}`);
      toast.success("Tour deleted");
      fetchData();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const handleSetCapacity = async () => {
    if (!inventoryTour || !inventoryDate || !inventoryCapacity) {
      toast.error("Select tour, date, and capacity");
      return;
    }
    try {
      await api.post(`/tours/${inventoryTour}/capacity`, {
        date: format(inventoryDate, "yyyy-MM-dd"),
        max_capacity: parseInt(inventoryCapacity),
      });
      toast.success("Capacity updated");
      if (inventoryTour) fetchInventoryAvailability(inventoryTour);
    } catch {
      toast.error("Failed to update capacity");
    }
  };

  return (
    <div className="admin-theme min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="font-cormorant text-2xl font-light" data-testid="admin-title">Admin Dashboard</h1>
          {user && <p className="font-manrope text-xs text-muted-foreground">{user.email}</p>}
        </div>
        <Button data-testid="admin-logout-btn" variant="outline" size="sm" onClick={handleLogout} className="font-manrope text-xs">
          <LogOut className="w-3 h-3 mr-2" /> Logout
        </Button>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        <Tabs defaultValue="analytics">
          <TabsList className="bg-muted mb-8">
            <TabsTrigger data-testid="admin-analytics-tab" value="analytics" className="font-manrope text-xs">Analytics</TabsTrigger>
            <TabsTrigger data-testid="admin-tours-tab" value="tours" className="font-manrope text-xs">Tours</TabsTrigger>
            <TabsTrigger data-testid="admin-bookings-tab" value="bookings" className="font-manrope text-xs">Bookings</TabsTrigger>
            <TabsTrigger data-testid="admin-inventory-tab" value="inventory" className="font-manrope text-xs">Inventory</TabsTrigger>
            <TabsTrigger data-testid="admin-pricing-tab" value="pricing" className="font-manrope text-xs">Pricing</TabsTrigger>
            <TabsTrigger data-testid="admin-leads-tab" value="leads" className="font-manrope text-xs">Leads</TabsTrigger>
            <TabsTrigger data-testid="admin-blog-tab" value="blog" className="font-manrope text-xs">Blog</TabsTrigger>
            <TabsTrigger data-testid="admin-banners-tab" value="banners" className="font-manrope text-xs">Banners</TabsTrigger>
          </TabsList>

          {/* ANALYTICS TAB */}
          <TabsContent value="analytics">
            <AnalyticsDashboard analytics={analytics} />
          </TabsContent>

          {/* TOURS TAB */}
          <TabsContent value="tours">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-cormorant text-xl font-light">Manage Tours ({tours.length})</h2>
              <Button data-testid="admin-add-tour-btn" onClick={openAdd} className="bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-xs rounded-full">
                <Plus className="w-3 h-3 mr-2" /> Add Tour
              </Button>
            </div>
            <div className="space-y-3">
              {tours.map((tour) => (
                <div key={tour.tour_id} className="flex items-center gap-4 p-4 bg-card border border-border" data-testid={`admin-tour-${tour.tour_id}`}>
                  <div className="w-16 h-12 overflow-hidden flex-shrink-0 bg-muted">
                    {tour.images?.[0] && <img src={getImageUrl(tour.images[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-manrope text-sm font-medium truncate">{tour.title}</p>
                    <p className="font-manrope text-xs text-muted-foreground">{tour.category} &middot; ${tour.price} &middot; {tour.difficulty}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`edit-tour-${tour.tour_id}`} variant="outline" size="sm" onClick={() => openEdit(tour)} className="font-manrope text-xs">
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button
                      data-testid={`delete-tour-${tour.tour_id}`}
                      variant="outline" size="sm"
                      disabled={deleting === tour.tour_id}
                      onClick={() => handleDelete(tour.tour_id)}
                      className="font-manrope text-xs text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
              {tours.length === 0 && (
                <p className="text-center py-12 text-muted-foreground font-manrope text-sm">No tours yet. Add your first tour!</p>
              )}
            </div>
          </TabsContent>

          {/* BOOKINGS TAB */}
          <TabsContent value="bookings">
            <h2 className="font-cormorant text-xl font-light mb-6">All Bookings ({bookings.length})</h2>
            <div className="space-y-3">
              {bookings.map((b) => {
                const statusStyles = {
                  confirmed: "bg-canopy/20 text-canopy",
                  cancelled: "bg-red-900/30 text-red-400",
                  pending: "bg-sandy/20 text-sandy",
                };
                return (
                  <div key={b.booking_id} className="p-4 bg-card border border-border" data-testid={`booking-${b.booking_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-manrope text-sm font-medium">{b.tour_title}</p>
                      <div className="flex items-center gap-2">
                        <Badge className={`font-manrope text-xs ${statusStyles[b.status] || ""}`}>{b.status}</Badge>
                        {b.refund_status && (
                          <Badge className={`font-manrope text-xs ${b.refund_status === "processed" ? "bg-canopy/20 text-canopy" : "bg-sandy/20 text-sandy"}`}>
                            Refund: {b.refund_status}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="font-manrope text-xs text-muted-foreground">
                      {b.customer_name} &middot; {b.customer_email} &middot; {b.date} &middot; {b.adults || b.guests} adult{(b.adults || b.guests) !== 1 ? "s" : ""}{b.children > 0 ? ` + ${b.children} child${b.children !== 1 ? "ren" : ""}` : ""} &middot; ${b.total_price}
                    </p>
                    {b.status === "cancelled" && (
                      <div className="mt-2 p-2 bg-muted rounded text-xs font-manrope">
                        <p className="text-muted-foreground">{b.refund_reason} &middot; Refund: <strong className="text-foreground">${b.refund_amount?.toFixed(2)}</strong></p>
                        {b.cancel_reason && <p className="text-muted-foreground mt-1">Reason: {b.cancel_reason}</p>}
                      </div>
                    )}
                    <div className="flex gap-2 mt-3">
                      {b.status === "confirmed" && (
                        <Button
                          data-testid={`cancel-booking-${b.booking_id}`}
                          variant="outline" size="sm"
                          onClick={() => openCancelDialog(b)}
                          className="font-manrope text-xs text-destructive hover:text-destructive"
                        >
                          <X className="w-3 h-3 mr-1" /> Cancel Booking
                        </Button>
                      )}
                      {b.status === "cancelled" && b.refund_status === "pending" && (
                        <Button
                          data-testid={`process-refund-${b.booking_id}`}
                          variant="outline" size="sm"
                          onClick={async () => {
                            try {
                              await api.post(`/bookings/${b.booking_id}/process-refund`);
                              toast.success("Refund marked as processed");
                              fetchData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed");
                            }
                          }}
                          className="font-manrope text-xs text-sandy hover:text-sandy"
                        >
                          Mark Refund Processed
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {bookings.length === 0 && (
                <p className="text-center py-12 text-muted-foreground font-manrope text-sm">No bookings yet.</p>
              )}
            </div>
          </TabsContent>

          {/* INVENTORY TAB */}
          <TabsContent value="inventory">
            <h2 className="font-cormorant text-xl font-light mb-6">Manage Capacity</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Select Tour</Label>
                <Select value={inventoryTour || ""} onValueChange={(val) => { setInventoryTour(val); setInventoryAvailability(null); fetchInventoryAvailability(val); }}>
                  <SelectTrigger data-testid="inventory-tour-select" className="font-manrope text-sm">
                    <SelectValue placeholder="Choose a tour" />
                  </SelectTrigger>
                  <SelectContent>
                    {tours.map(t => (
                      <SelectItem key={t.tour_id} value={t.tour_id}>{t.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="mt-6">
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Select Date</Label>
                  <Calendar
                    mode="single"
                    selected={inventoryDate}
                    onSelect={setInventoryDate}
                    disabled={(d) => d < new Date()}
                    modifiers={inventoryAvailability ? {
                      hasBookings: (date) => {
                        const dateStr = format(date, "yyyy-MM-dd");
                        return !!(inventoryAvailability.dates?.[dateStr]);
                      },
                      fullyBooked: (date) => {
                        const dateStr = format(date, "yyyy-MM-dd");
                        const info = inventoryAvailability.dates?.[dateStr];
                        return info && info.available <= 0;
                      },
                    } : {}}
                    modifiersStyles={{
                      hasBookings: { backgroundColor: 'rgba(197, 160, 89, 0.2)', color: '#C5A059', fontWeight: '600' },
                      fullyBooked: { backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#DC2626', fontWeight: '600' },
                    }}
                    className="border border-border"
                    data-testid="inventory-calendar"
                  />
                  {inventoryDate && inventoryAvailability && (
                    <div className="mt-2 p-3 bg-muted rounded" data-testid="inventory-date-info">
                      {(() => {
                        const dateStr = format(inventoryDate, "yyyy-MM-dd");
                        const info = inventoryAvailability.dates?.[dateStr];
                        const cap = inventoryAvailability.overrides?.[dateStr] || inventoryAvailability.default_max_capacity;
                        const booked = info?.booked || 0;
                        return (
                          <div className="font-manrope text-xs space-y-1">
                            <p className="text-muted-foreground">{format(inventoryDate, "MMMM d, yyyy")}</p>
                            <p>Capacity: <strong className="text-foreground">{cap}</strong></p>
                            <p>Booked: <strong className="text-foreground">{booked}</strong></p>
                            <p>Available: <strong className={booked >= cap ? "text-red-400" : "text-green-400"}>{Math.max(0, cap - booked)}</strong></p>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Max Capacity for Date</Label>
                <Input
                  data-testid="inventory-capacity-input"
                  type="number"
                  value={inventoryCapacity}
                  onChange={(e) => setInventoryCapacity(e.target.value)}
                  placeholder="e.g. 15"
                  className="font-manrope text-sm mb-4"
                />
                {inventoryDate && (
                  <p className="font-manrope text-xs text-muted-foreground mb-4">
                    Setting capacity for {format(inventoryDate, "MMMM d, yyyy")}
                  </p>
                )}
                <Button
                  data-testid="inventory-save-btn"
                  onClick={handleSetCapacity}
                  className="bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-xs rounded-full"
                >
                  Save Capacity
                </Button>

                {/* Availability Overview */}
                {inventoryAvailability && Object.keys(inventoryAvailability.dates || {}).length > 0 && (
                  <div className="mt-8" data-testid="inventory-overview">
                    <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground mb-3 block">Dates with Bookings</Label>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {Object.entries(inventoryAvailability.dates).sort().map(([date, info]) => (
                        <div key={date} className="flex items-center justify-between p-2 bg-muted rounded text-xs font-manrope">
                          <span className="text-foreground">{date}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-muted-foreground">{info.booked}/{info.max_capacity} booked</span>
                            <Badge className={info.available <= 0 ? "bg-red-900/30 text-red-400" : info.available <= 3 ? "bg-sandy/20 text-sandy" : "bg-canopy/20 text-canopy"}>
                              {info.available <= 0 ? "Full" : `${info.available} left`}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          {/* PRICING TAB */}
          <TabsContent value="pricing">
            <h2 className="font-cormorant text-xl font-light mb-6">Dynamic Pricing Rules</h2>
            {pricingRules && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-6 bg-card border border-border" data-testid="early-bird-settings">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-manrope text-sm font-medium">Early Bird Discount</p>
                      <p className="font-manrope text-xs text-muted-foreground">For bookings made well in advance</p>
                    </div>
                    <Switch
                      data-testid="early-bird-toggle"
                      checked={pricingRules.early_bird_enabled}
                      onCheckedChange={(v) => setPricingRules({...pricingRules, early_bird_enabled: v})}
                    />
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="font-manrope text-xs text-muted-foreground">Discount (%)</Label>
                      <Input
                        data-testid="early-bird-discount-input"
                        type="number"
                        value={pricingRules.early_bird_discount}
                        onChange={(e) => setPricingRules({...pricingRules, early_bird_discount: parseFloat(e.target.value) || 0})}
                        className="mt-1 font-manrope text-sm"
                      />
                    </div>
                    <div>
                      <Label className="font-manrope text-xs text-muted-foreground">Days in advance (minimum)</Label>
                      <Input
                        data-testid="early-bird-days-input"
                        type="number"
                        value={pricingRules.early_bird_days}
                        onChange={(e) => setPricingRules({...pricingRules, early_bird_days: parseInt(e.target.value) || 30})}
                        className="mt-1 font-manrope text-sm"
                      />
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-card border border-border" data-testid="last-minute-settings">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-manrope text-sm font-medium">Last Minute Discount</p>
                      <p className="font-manrope text-xs text-muted-foreground">When capacity is running low</p>
                    </div>
                    <Switch
                      data-testid="last-minute-toggle"
                      checked={pricingRules.last_minute_enabled}
                      onCheckedChange={(v) => setPricingRules({...pricingRules, last_minute_enabled: v})}
                    />
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="font-manrope text-xs text-muted-foreground">Discount (%)</Label>
                      <Input
                        data-testid="last-minute-discount-input"
                        type="number"
                        value={pricingRules.last_minute_discount}
                        onChange={(e) => setPricingRules({...pricingRules, last_minute_discount: parseFloat(e.target.value) || 0})}
                        className="mt-1 font-manrope text-sm"
                      />
                    </div>
                    <div>
                      <Label className="font-manrope text-xs text-muted-foreground">Capacity threshold (0-1, e.g. 0.3 = 30%)</Label>
                      <Input
                        data-testid="last-minute-threshold-input"
                        type="number"
                        step="0.05"
                        value={pricingRules.last_minute_capacity_threshold}
                        onChange={(e) => setPricingRules({...pricingRules, last_minute_capacity_threshold: parseFloat(e.target.value) || 0.3})}
                        className="mt-1 font-manrope text-sm"
                      />
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Button
                    data-testid="save-pricing-rules-btn"
                    onClick={async () => {
                      try {
                        await api.put("/pricing-rules", pricingRules);
                        toast.success("Pricing rules saved");
                      } catch { toast.error("Failed to save"); }
                    }}
                    className="bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-xs rounded-full"
                  >
                    Save Pricing Rules
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* LEADS TAB */}
          <TabsContent value="leads">
            <h2 className="font-cormorant text-xl font-light mb-6">Email Leads ({leads.length})</h2>
            <div className="space-y-3">
              {leads.map((lead) => (
                <div key={lead.lead_id} className="flex items-center justify-between p-4 bg-card border border-border" data-testid={`lead-${lead.lead_id}`}>
                  <div>
                    <p className="font-manrope text-sm">{lead.email}</p>
                    <p className="font-manrope text-xs text-muted-foreground">
                      Code: <span className="font-mono">{lead.discount_code}</span> &middot; {lead.discount_percent}% off
                    </p>
                  </div>
                  <Badge className={lead.used ? "bg-muted text-muted-foreground" : "bg-canopy/20 text-canopy"}>
                    {lead.used ? "Used" : "Active"}
                  </Badge>
                </div>
              ))}
              {leads.length === 0 && (
                <p className="text-center py-12 text-muted-foreground font-manrope text-sm">No leads captured yet.</p>
              )}
            </div>
          </TabsContent>

          {/* BLOG TAB */}
          <TabsContent value="blog">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-cormorant text-xl font-light">Manage Blog ({blogPosts.length})</h2>
              <Button data-testid="admin-add-post-btn" onClick={() => { setBlogMode("add"); setEditPost(null); setBlogTitle(""); setBlogExcerpt(""); setBlogImage(""); setBlogCategory("Travel Guide"); setBlogReadTime("5 min read"); setBlogContent(""); setBlogFormOpen(true); }} className="bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-xs rounded-full">
                <Plus className="w-3 h-3 mr-2" /> Add Post
              </Button>
            </div>
            <div className="space-y-3">
              {blogPosts.map((post) => (
                <div key={post.slug} className="flex items-center gap-4 p-4 bg-card border border-border" data-testid={`admin-post-${post.slug}`}>
                  <div className="w-16 h-12 overflow-hidden flex-shrink-0 bg-muted">
                    {post.image && <img src={post.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-manrope text-sm font-medium truncate">{post.title}</p>
                    <p className="font-manrope text-xs text-muted-foreground">{post.category} &middot; {post.read_time}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`edit-post-${post.slug}`} variant="outline" size="sm" onClick={() => { setBlogMode("edit"); setEditPost(post); setBlogTitle(post.title); setBlogExcerpt(post.excerpt || ""); setBlogImage(post.image || ""); setBlogCategory(post.category || "Travel Guide"); setBlogReadTime(post.read_time || "5 min read"); setBlogContent(post.content || ""); setBlogFormOpen(true); }} className="font-manrope text-xs">
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button data-testid={`delete-post-${post.slug}`} variant="outline" size="sm" onClick={async () => { try { await api.delete(`/blog/${post.slug}`); toast.success("Post deleted"); fetchData(); } catch { toast.error("Delete failed"); }}} className="font-manrope text-xs text-destructive hover:text-destructive">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
              {blogPosts.length === 0 && <p className="text-center py-12 text-muted-foreground font-manrope text-sm">No blog posts yet.</p>}
            </div>
          </TabsContent>

          {/* BANNERS TAB */}
          <TabsContent value="banners">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-cormorant text-xl font-light">Promotional Banners ({banners.length})</h2>
              <Button data-testid="admin-add-banner-btn" onClick={() => { setBannerMode("add"); setEditBanner(null); setBannerTitle(""); setBannerDesc(""); setBannerCode(""); setBannerPercent(""); setBannerBg("#0F172A"); setBannerText("#C5A059"); setBannerStart(""); setBannerEnd(""); setBannerEnabled(true); setBannerFormOpen(true); }} className="bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-xs rounded-full">
                <Plus className="w-3 h-3 mr-2" /> Add Banner
              </Button>
            </div>
            <div className="space-y-3">
              {banners.map((b) => {
                const isActive = b.enabled && new Date(b.start_date) <= new Date() && new Date(b.end_date) >= new Date();
                return (
                  <div key={b.banner_id} className="p-4 bg-card border border-border" data-testid={`admin-banner-${b.banner_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-4 rounded" style={{ backgroundColor: b.bg_color }} />
                        <p className="font-manrope text-sm font-medium">{b.title}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={isActive ? "bg-canopy/20 text-canopy" : b.enabled ? "bg-sandy/20 text-sandy" : "bg-muted text-muted-foreground"}>
                          {isActive ? "Live" : b.enabled ? "Scheduled" : "Disabled"}
                        </Badge>
                      </div>
                    </div>
                    <p className="font-manrope text-xs text-muted-foreground mb-2">
                      {b.description && `${b.description} | `}{b.discount_code && `Code: ${b.discount_code} | `}{b.start_date?.slice(0,10)} to {b.end_date?.slice(0,10)}
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={async () => { try { await api.put(`/banners/${b.banner_id}`, { enabled: !b.enabled }); toast.success(b.enabled ? "Banner disabled" : "Banner enabled"); fetchData(); } catch { toast.error("Failed"); }}} className="font-manrope text-xs" data-testid={`toggle-banner-${b.banner_id}`}>
                        {b.enabled ? "Disable" : "Enable"}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setBannerMode("edit"); setEditBanner(b); setBannerTitle(b.title); setBannerDesc(b.description || ""); setBannerCode(b.discount_code || ""); setBannerPercent(String(b.discount_percent || "")); setBannerBg(b.bg_color || "#0F172A"); setBannerText(b.text_color || "#C5A059"); setBannerStart(b.start_date?.slice(0,16) || ""); setBannerEnd(b.end_date?.slice(0,16) || ""); setBannerEnabled(b.enabled); setBannerFormOpen(true); }} className="font-manrope text-xs" data-testid={`edit-banner-${b.banner_id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={async () => { try { await api.delete(`/banners/${b.banner_id}`); toast.success("Banner deleted"); fetchData(); } catch { toast.error("Failed"); }}} className="font-manrope text-xs text-destructive hover:text-destructive" data-testid={`delete-banner-${b.banner_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              {banners.length === 0 && <p className="text-center py-12 text-muted-foreground font-manrope text-sm">No promotional banners yet.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Banner Form Dialog */}
      <Dialog open={bannerFormOpen} onOpenChange={setBannerFormOpen}>
        <DialogContent className="max-w-lg admin-theme" data-testid="banner-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-cormorant text-2xl font-light">{bannerMode === "add" ? "Create Banner" : "Edit Banner"}</DialogTitle>
            <DialogDescription className="font-manrope text-sm text-muted-foreground">Set up a promotional banner for special periods.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Banner Title *</Label>
              <Input data-testid="banner-form-title" value={bannerTitle} onChange={e => setBannerTitle(e.target.value)} placeholder="e.g. Holiday Special - 20% Off!" className="mt-1 font-manrope text-sm" />
            </div>
            <div>
              <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Description</Label>
              <Input data-testid="banner-form-desc" value={bannerDesc} onChange={e => setBannerDesc(e.target.value)} placeholder="e.g. Book any tour this weekend" className="mt-1 font-manrope text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Discount Code</Label>
                <Input data-testid="banner-form-code" value={bannerCode} onChange={e => setBannerCode(e.target.value.toUpperCase())} placeholder="e.g. HOLIDAY20" className="mt-1 font-manrope text-sm" />
              </div>
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Discount %</Label>
                <Input data-testid="banner-form-percent" type="number" value={bannerPercent} onChange={e => setBannerPercent(e.target.value)} className="mt-1 font-manrope text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Start Date *</Label>
                <Input data-testid="banner-form-start" type="datetime-local" value={bannerStart} onChange={e => setBannerStart(e.target.value)} className="mt-1 font-manrope text-sm" />
              </div>
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">End Date *</Label>
                <Input data-testid="banner-form-end" type="datetime-local" value={bannerEnd} onChange={e => setBannerEnd(e.target.value)} className="mt-1 font-manrope text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Background Color</Label>
                <div className="flex gap-2 mt-1">
                  <input type="color" value={bannerBg} onChange={e => setBannerBg(e.target.value)} className="w-10 h-9 border border-border cursor-pointer" />
                  <Input value={bannerBg} onChange={e => setBannerBg(e.target.value)} className="font-manrope text-sm font-mono" />
                </div>
              </div>
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Text Color</Label>
                <div className="flex gap-2 mt-1">
                  <input type="color" value={bannerText} onChange={e => setBannerText(e.target.value)} className="w-10 h-9 border border-border cursor-pointer" />
                  <Input value={bannerText} onChange={e => setBannerText(e.target.value)} className="font-manrope text-sm font-mono" />
                </div>
              </div>
            </div>
            {/* Preview */}
            <div className="p-3 text-center text-sm font-manrope" style={{ backgroundColor: bannerBg, color: bannerText }}>
              {bannerTitle || "Banner Preview"} {bannerDesc && <span className="opacity-80 ml-1">{bannerDesc}</span>} {bannerCode && <span className="ml-2 px-2 py-0.5 bg-white/10 font-mono text-xs">{bannerCode}</span>}
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="banner-form-enabled" checked={bannerEnabled} onCheckedChange={setBannerEnabled} />
              <span className="font-manrope text-sm">{bannerEnabled ? "Enabled" : "Disabled"}</span>
            </div>
            <Button data-testid="banner-form-save-btn" onClick={async () => {
              if (!bannerTitle || !bannerStart || !bannerEnd) { toast.error("Title, start and end dates required"); return; }
              setBannerSaving(true);
              try {
                const payload = { title: bannerTitle, description: bannerDesc, discount_code: bannerCode, discount_percent: parseFloat(bannerPercent) || 0, bg_color: bannerBg, text_color: bannerText, start_date: bannerStart, end_date: bannerEnd, enabled: bannerEnabled };
                if (bannerMode === "add") { await api.post("/banners", payload); toast.success("Banner created"); }
                else { await api.put(`/banners/${editBanner.banner_id}`, payload); toast.success("Banner updated"); }
                setBannerFormOpen(false); fetchData();
              } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
              finally { setBannerSaving(false); }
            }} disabled={bannerSaving} className="w-full bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-sm rounded-full h-10">
              {bannerSaving ? "Saving..." : bannerMode === "add" ? "Create Banner" : "Update Banner"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Blog Form Dialog */}
      <Dialog open={blogFormOpen} onOpenChange={setBlogFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] admin-theme" data-testid="blog-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-cormorant text-2xl font-light">{blogMode === "add" ? "Add Blog Post" : "Edit Blog Post"}</DialogTitle>
            <DialogDescription className="font-manrope text-sm text-muted-foreground">Write a travel guide or article for the blog.</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-4">
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Title *</Label>
                <Input data-testid="blog-form-title" value={blogTitle} onChange={e => setBlogTitle(e.target.value)} className="mt-1 font-manrope text-sm" />
              </div>
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Excerpt</Label>
                <Textarea data-testid="blog-form-excerpt" value={blogExcerpt} onChange={e => setBlogExcerpt(e.target.value)} rows={2} className="mt-1 font-manrope text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Category</Label>
                  <Select value={blogCategory} onValueChange={setBlogCategory}>
                    <SelectTrigger data-testid="blog-form-category" className="mt-1 font-manrope text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Travel Guide">Travel Guide</SelectItem>
                      <SelectItem value="Things to Do">Things to Do</SelectItem>
                      <SelectItem value="Travel Tips">Travel Tips</SelectItem>
                      <SelectItem value="Adventure Guide">Adventure Guide</SelectItem>
                      <SelectItem value="Food & Culture">Food & Culture</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Read Time</Label>
                  <Input data-testid="blog-form-readtime" value={blogReadTime} onChange={e => setBlogReadTime(e.target.value)} className="mt-1 font-manrope text-sm" />
                </div>
              </div>
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Cover Image URL</Label>
                <Input data-testid="blog-form-image" value={blogImage} onChange={e => setBlogImage(e.target.value)} placeholder="https://..." className="mt-1 font-manrope text-sm" />
              </div>
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Content *</Label>
                <Textarea data-testid="blog-form-content" value={blogContent} onChange={e => setBlogContent(e.target.value)} rows={12} placeholder="Use **bold** for headings. Separate paragraphs with blank lines." className="mt-1 font-manrope text-sm font-mono" />
              </div>
              <Separator />
              <Button data-testid="blog-form-save-btn" onClick={async () => {
                if (!blogTitle || !blogContent) { toast.error("Title and content required"); return; }
                setBlogSaving(true);
                try {
                  const payload = { title: blogTitle, excerpt: blogExcerpt, image: blogImage, category: blogCategory, read_time: blogReadTime, content: blogContent };
                  if (blogMode === "add") { await api.post("/blog", payload); toast.success("Post created"); }
                  else { const slug = editPost?.slug || editPost?.post_id; if (!slug) { toast.error("Cannot identify post to update"); setBlogSaving(false); return; } await api.put(`/blog/${slug}`, payload); toast.success("Post updated"); }
                  setBlogFormOpen(false); fetchData();
                } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
                finally { setBlogSaving(false); }
              }} disabled={blogSaving} className="w-full bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-sm rounded-full h-10">
                {blogSaving ? "Saving..." : blogMode === "add" ? "Publish Post" : "Update Post"}
              </Button>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md admin-theme" data-testid="cancel-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-cormorant text-2xl font-light">Cancel Booking</DialogTitle>
            <DialogDescription className="font-manrope text-sm text-muted-foreground">Review the refund calculation before confirming.</DialogDescription>
          </DialogHeader>
          {cancelBooking && (() => {
            const refundInfo = getCancelRefundInfo(cancelBooking);
            const finalAmount = cancelFullRefund ? cancelBooking.total_price : refundInfo.amount;
            const finalPercent = cancelFullRefund ? 100 : refundInfo.percent;
            const finalReason = cancelFullRefund ? "Full refund (admin override)" : refundInfo.reason;
            return (
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded">
                  <p className="font-manrope text-sm font-medium">{cancelBooking.tour_title}</p>
                  <p className="font-manrope text-xs text-muted-foreground mt-1">
                    {cancelBooking.customer_name} &middot; {cancelBooking.date} &middot; ${cancelBooking.total_price}
                  </p>
                </div>
                <div className="p-4 border border-border rounded">
                  <p className="font-manrope text-xs uppercase tracking-widest text-muted-foreground mb-2">Refund Calculation (per policy)</p>
                  <p className="font-manrope text-xs text-muted-foreground mb-1">{refundInfo.reason}</p>
                  <p className="font-manrope text-sm">Policy refund: <strong className={refundInfo.percent === 0 ? "text-red-400" : "text-foreground"}>${refundInfo.amount.toFixed(2)} ({refundInfo.percent}%)</strong></p>
                </div>
                <div className="flex items-center gap-3 p-3 border border-sandy/20 rounded">
                  <Switch
                    data-testid="cancel-full-refund-toggle"
                    checked={cancelFullRefund}
                    onCheckedChange={setCancelFullRefund}
                  />
                  <div>
                    <p className="font-manrope text-sm text-foreground">Override: Grant full refund</p>
                    <p className="font-manrope text-[10px] text-muted-foreground">For weather cancellations or operational issues</p>
                  </div>
                </div>
                <div className="p-4 bg-muted rounded">
                  <div className="flex justify-between items-center">
                    <span className="font-manrope text-sm text-muted-foreground">Final Refund</span>
                    <span className="font-cormorant text-2xl text-foreground" data-testid="cancel-refund-amount">${finalAmount.toFixed(2)} <span className="text-sm text-muted-foreground">({finalPercent}%)</span></span>
                  </div>
                  <p className="font-manrope text-[10px] text-muted-foreground mt-1">{finalReason}</p>
                </div>
                <div>
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Cancellation Reason</Label>
                  <Input
                    data-testid="cancel-reason-input"
                    value={cancelReason}
                    onChange={e => setCancelReason(e.target.value)}
                    placeholder="e.g. Customer request, weather, etc."
                    className="mt-1 font-manrope text-sm"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    data-testid="cancel-confirm-btn"
                    onClick={handleCancelConfirm}
                    disabled={cancelling}
                    className="flex-1 bg-red-600 text-white hover:bg-red-700 font-manrope text-sm rounded-full h-10"
                  >
                    {cancelling ? "Cancelling..." : `Cancel & Refund $${finalAmount.toFixed(2)}`}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setCancelDialogOpen(false)}
                    className="font-manrope text-sm rounded-full"
                  >
                    Keep Booking
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Tour Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] admin-theme" data-testid="tour-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-cormorant text-2xl font-light">
              {formMode === "add" ? "Add New Tour" : "Edit Tour"}
            </DialogTitle>
            <DialogDescription className="font-manrope text-sm text-muted-foreground">
              {formMode === "add" ? "Create a new tour for the storefront." : "Update the tour details."}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-4">
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Title *</Label>
                <Input data-testid="tour-form-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 font-manrope text-sm" />
              </div>
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Short Description *</Label>
                <Textarea data-testid="tour-form-description" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 font-manrope text-sm" rows={2} />
              </div>
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Long Description</Label>
                <Textarea data-testid="tour-form-long-description" value={longDescription} onChange={(e) => setLongDescription(e.target.value)} className="mt-1 font-manrope text-sm" rows={4} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Category *</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="tour-form-category" className="mt-1 font-manrope text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Land">Land</SelectItem>
                      <SelectItem value="Sea">Sea</SelectItem>
                      <SelectItem value="Adventure">Adventure</SelectItem>
                      <SelectItem value="Epic Combos">Epic Combos</SelectItem>
                      <SelectItem value="Kids Package">Kids Package</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Difficulty *</Label>
                  <Select value={difficulty} onValueChange={setDifficulty}>
                    <SelectTrigger data-testid="tour-form-difficulty" className="mt-1 font-manrope text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Easy">Easy</SelectItem>
                      <SelectItem value="Moderate">Moderate</SelectItem>
                      <SelectItem value="Hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Price ($) *</Label>
                  <Input data-testid="tour-form-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 font-manrope text-sm" />
                </div>
                <div>
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Duration *</Label>
                  <Input data-testid="tour-form-duration" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g. 5 hours" className="mt-1 font-manrope text-sm" />
                </div>
                <div>
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Duration (hours)</Label>
                  <Input data-testid="tour-form-duration-hours" type="number" value={durationHours} onChange={(e) => setDurationHours(e.target.value)} className="mt-1 font-manrope text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Max Capacity</Label>
                  <Input data-testid="tour-form-max-capacity" type="number" value={maxCapacity} onChange={(e) => setMaxCapacity(e.target.value)} className="mt-1 font-manrope text-sm" />
                </div>
                <div>
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Meeting Point</Label>
                  <Input data-testid="tour-form-meeting-point" value={meetingPoint} onChange={(e) => setMeetingPoint(e.target.value)} className="mt-1 font-manrope text-sm" />
                </div>
              </div>

              {/* Children & Child Pricing */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Children Allowed</Label>
                  <Select value={childrenAllowed ? "yes" : "no"} onValueChange={(v) => setChildrenAllowed(v === "yes")}>
                    <SelectTrigger data-testid="tour-form-children-allowed" className="mt-1 font-manrope text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground">Child Price ($)</Label>
                  <Input data-testid="tour-form-child-price" type="number" value={childPrice} onChange={(e) => setChildPrice(e.target.value)} placeholder="0 = free" className="mt-1 font-manrope text-sm" disabled={!childrenAllowed} />
                </div>
              </div>

              {/* Images */}
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Images</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {images.map((img, i) => (
                    <div key={i} className="relative w-20 h-16 overflow-hidden border border-border">
                      <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/60 p-0.5">
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
                <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-border cursor-pointer hover:border-sandy transition-colors duration-200">
                  <Upload className="w-3 h-3" />
                  <span className="font-manrope text-xs">{uploading ? "Uploading..." : "Upload Image"}</span>
                  <input data-testid="tour-form-image-upload" type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
                </label>
              </div>

              {/* What's Included */}
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground mb-2 block">What's Included</Label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {whatsIncluded.map((item, i) => (
                    <Badge key={i} variant="outline" className="font-manrope text-xs">
                      {item} <button onClick={() => setWhatsIncluded(whatsIncluded.filter((_, j) => j !== i))} className="ml-1"><X className="w-2 h-2" /></button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input data-testid="tour-form-included-input" value={newIncluded} onChange={(e) => setNewIncluded(e.target.value)} placeholder="Add item" className="font-manrope text-xs" onKeyDown={(e) => { if (e.key === 'Enter' && newIncluded.trim()) { e.preventDefault(); setWhatsIncluded([...whatsIncluded, newIncluded.trim()]); setNewIncluded(""); }}} />
                  <Button data-testid="tour-form-add-included-btn" type="button" variant="outline" size="sm" onClick={() => { if (newIncluded.trim()) { setWhatsIncluded([...whatsIncluded, newIncluded.trim()]); setNewIncluded(""); }}} className="font-manrope text-xs">Add</Button>
                </div>
              </div>

              {/* Highlights */}
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Highlights</Label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {highlights.map((item, i) => (
                    <Badge key={i} variant="outline" className="font-manrope text-xs">
                      {item} <button onClick={() => setHighlights(highlights.filter((_, j) => j !== i))} className="ml-1"><X className="w-2 h-2" /></button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input data-testid="tour-form-highlight-input" value={newHighlight} onChange={(e) => setNewHighlight(e.target.value)} placeholder="Add highlight" className="font-manrope text-xs" onKeyDown={(e) => { if (e.key === 'Enter' && newHighlight.trim()) { e.preventDefault(); setHighlights([...highlights, newHighlight.trim()]); setNewHighlight(""); }}} />
                  <Button data-testid="tour-form-add-highlight-btn" type="button" variant="outline" size="sm" onClick={() => { if (newHighlight.trim()) { setHighlights([...highlights, newHighlight.trim()]); setNewHighlight(""); }}} className="font-manrope text-xs">Add</Button>
                </div>
              </div>

              <Separator />

              <Button data-testid="tour-form-save-btn" onClick={handleSave} disabled={saving} className="w-full bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-sm rounded-full h-10">
                {saving ? "Saving..." : formMode === "add" ? "Create Tour" : "Update Tour"}
              </Button>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
